import React, { useContext, useMemo } from 'react';

import { RouteChrome } from './types';

const FULL_CHROME: Required<RouteChrome> = { sidebar: true, topBar: true, search: true };

export const CHROMELESS: RouteChrome = { sidebar: false, topBar: false };

const RouteChromeContext = React.createContext<RouteChrome>({});

const statedOnly = (chrome?: RouteChrome): RouteChrome => {
  if (!chrome) {
    return {};
  }

  const { sidebar, topBar, search } = chrome;

  return {
    ...(sidebar === undefined ? null : { sidebar }),
    ...(topBar === undefined ? null : { topBar }),
    ...(search === undefined ? null : { search }),
  };
};

export const RouteChromeProvider: React.FC<React.PropsWithChildren<{ chrome?: RouteChrome }>> = ({
  chrome,
  children,
}) => {
  const inherited = useContext(RouteChromeContext);
  const { sidebar, topBar, search } = chrome ?? {};

  const value = useMemo(
    () => ({ ...inherited, ...statedOnly({ sidebar, topBar, search }) }),
    [inherited, sidebar, topBar, search],
  );

  return <RouteChromeContext.Provider value={value}>{children}</RouteChromeContext.Provider>;
};

export const useRouteChromeOverrides = (): RouteChrome => useContext(RouteChromeContext);

export const useRouteChrome = (): Required<RouteChrome> => {
  const overrides = useRouteChromeOverrides();

  return useMemo(() => {
    const merged = { ...FULL_CHROME, ...overrides };

    return merged.topBar ? merged : { ...merged, search: false };
  }, [overrides]);
};

export default RouteChromeProvider;
